import { useEffect, type ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import { EmptyState, EventsBucket, EventsShowMore } from '../_shared';
import type { AuthState } from '../_shared/EventsBucket/EventsBucket';
import type { EventsDisplayMode } from '../_shared/EventsViewToggle/EventsViewToggle';
import { usePastEvents } from './usePastEvents';

const messages = defineMessages({
  emptyTitle: {
    id: 'No past events yet',
    defaultMessage: 'No past events yet',
  },
  emptySubtitle: {
    id: 'Follow this organizer to be the first to know when they host an event',
    defaultMessage: 'Follow this organizer to be the first to know when they host an event',
  },
});

interface Props {
  organizerId: string;
  eventbriteBaseUrl: string;
  locale: string;
  eventsDisplayMode: EventsDisplayMode;
  onPastEventsAvailabilityChange?: (state: { isReady: boolean; hasEvents: boolean }) => void;
  selectedDateFilter?: string | null;
  mobileCalendarControl?: ReactNode;
  authState: AuthState;
}

export default function PastEvents(props: Props) {
  const { events, isLoading, isError, fetchNextPage, hasMorePages, isFetchingNextPage } =
    usePastEvents(props.organizerId, props.selectedDateFilter);

  useEffect(() => {
    if (isError) {
      return;
    }
    props.onPastEventsAvailabilityChange?.({
      isReady: !isLoading,
      hasEvents: events.length > 0,
    });
  }, [isError, isLoading, events.length, props.onPastEventsAvailabilityChange]);

  if (isError) {
    return null;
  }

  if (!isLoading && events.length === 0) {
    return (
      <>
        {props.mobileCalendarControl}
        <EmptyState
          organizerId={props.organizerId}
          eventbriteBaseUrl={props.eventbriteBaseUrl}
          title={messages.emptyTitle}
          subtitle={messages.emptySubtitle}
        />
      </>
    );
  }

  return (
    <div>
      <EventsBucket
        events={events}
        loadingState={{ isFeLoading: isLoading }}
        bucketName="past-events"
        locale={props.locale}
        organizerId={props.organizerId}
        variant={props.eventsDisplayMode}
        groupByDay={Boolean(props.selectedDateFilter)}
        mobileHeaderAccessory={props.mobileCalendarControl}
        authState={props.authState}
        showSaveAction
      />
      {hasMorePages && !isLoading && (
        <EventsShowMore onShowMore={() => fetchNextPage()} isLoadingMore={isFetchingNextPage} />
      )}
    </div>
  );
}
